/**
 * The list primitive: pagination, sorting, search.
 *
 * One component because all five hierarchy lists have the same requirements and the same
 * hazards. The important one is that **sorting is server-driven and column-whitelisted**:
 * `campaigns` is indexed on five fields, and a table that sorted by whatever column was
 * clicked would put an unindexed sort of 16,317 documents on production. Columns declare
 * whether they are sortable, so the UI cannot ask for what the database cannot afford.
 */

import { useEffect, useRef, useState } from 'react';
import type { ListParams, Page } from '../lib/api';

export interface Column<T> {
  key: string;
  label: string;
  /** Server-side sort field. Omit to make the column unsortable. */
  sortField?: string;
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
  width?: string;
}

export function DataTable<T>({
  title, columns, load, initialSort, initialDirection, searchPlaceholder, emptyMessage, onRowClick,
}: {
  title?: string;
  columns: Column<T>[];
  load: (params: ListParams) => Promise<Page<T>>;
  initialSort?: string;
  initialDirection?: 'asc' | 'desc';
  searchPlaceholder?: string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}) {
  const [data, setData] = useState<Page<T> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [params, setParams] = useState<ListParams>({
    page: 1, pageSize: 50, sort: initialSort, direction: initialDirection ?? 'desc', search: '',
  });
  const [searchText, setSearchText] = useState('');

  /*
   * Debounced search.
   *
   * Every keystroke would otherwise be a regex scan of 16,317 campaigns. 300ms turns a
   * typed word into one query instead of nine.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams((p) => (p.search === searchText ? p : { ...p, search: searchText, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Guards against an earlier, slower response overwriting a later one.
  const requestId = useRef(0);
  useEffect(() => {
    const id = ++requestId.current;
    setBusy(true);
    load(params)
      .then((page) => { if (id === requestId.current) { setData(page); setError(null); } })
      .catch((e: Error) => { if (id === requestId.current) setError(e.message); })
      .finally(() => { if (id === requestId.current) setBusy(false); });
  }, [params, load]);

  const toggleSort = (field: string) => {
    setParams((p) => ({
      ...p, page: 1, sort: field,
      direction: p.sort === field && p.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    /*
     * `wide` lives on the table itself, not on each screen that uses one.
     *
     * The 1,180px column suits a detail page and starves a table — which is what the rule was
     * written for. Campaigns opted in by wrapping itself; Clients and Agencies rendered a table
     * bare and sat in the narrow column with the same eleven columns to fit. Putting it here
     * means a list screen is wide because it IS a list, rather than because someone remembered.
     */
    <section className="table-block wide">
      <header className="table-head">
        {title && <h2>{title}</h2>}
        <input
          className="search"
          type="search"
          placeholder={searchPlaceholder ?? 'Search…'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </header>

      {error && <div className="banner error">{error}</div>}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width, textAlign: c.align ?? 'left' }}>
                  {c.sortField ? (
                    <button className="sortable" onClick={() => toggleSort(c.sortField!)}>
                      {c.label}
                      {data?.sort === c.sortField && (
                        <span className="arrow">{data.direction === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((row, i) => (
              <tr
                key={i}
                onClick={onRowClick ? (e) => {
                  // Don't treat a text selection as a click — otherwise highlighting a cell to
                  // copy it navigates into the row. A genuine click collapses the selection to an
                  // empty caret, so this only suppresses drag-selects.
                  if (window.getSelection()?.toString().trim()) return;
                  // Let interactive controls in a cell (links, the status toggle) handle their own
                  // click instead of bubbling up to a row navigation.
                  if ((e.target as HTMLElement).closest('a, button, input, select, label, textarea')) return;
                  onRowClick(row);
                } : undefined}
                className={onRowClick ? 'clickable' : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>{c.render(row)}</td>
                ))}
              </tr>
            ))}
            {data && data.rows.length === 0 && !busy && (
              <tr><td colSpan={columns.length} className="empty">{emptyMessage ?? 'Nothing found.'}</td></tr>
            )}
          </tbody>
        </table>
        {busy && <div className="busy">Loading…</div>}
      </div>

      {data && (
        <footer className="pager">
          <span className="count">
            {data.total.toLocaleString('en-US')} total
            {data.total > 0 && ` · page ${data.page} of ${totalPages.toLocaleString('en-US')}`}
          </span>
          <span className="controls">
            <button disabled={data.page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: 1 }))}>« First</button>
            <button disabled={data.page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}>‹ Prev</button>
            <button disabled={data.page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}>Next ›</button>
            <select value={data.pageSize}
              onChange={(e) => setParams((p) => ({ ...p, pageSize: Number(e.target.value), page: 1 }))}>
              {[25, 50, 100, 300].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </span>
        </footer>
      )}
    </section>
  );
}
